import React from "react";
import {TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useTranslation} from "react-i18next";
import CNumberField from "crqlar-react-common/dist/components/CNumberField";
import {
    clampWritingAreaHeightMm,
    getWritingAreaAttrs,
    MAX_WRITING_AREA_HEIGHT_MM,
    MIN_WRITING_AREA_HEIGHT_MM,
    WRITING_AREA_STYLES,
} from "../../../blocks/writingArea/printoutWritingAreaOptions";
import type {
    PrintoutWritingAreaAttrs,
    WritingAreaStyle,
} from "../../../blocks/writingArea/printoutWritingAreaOptions";
import {PanelField} from "../PrintoutPanelSection";
import {updateSelectedPrintoutBlockAttributes} from "./printoutBlockAttributes";
import type {PrintoutBlockSettingsProps} from "./printoutBlockAttributes";

const STYLE_LABELS: Record<WritingAreaStyle, string> = {
    blank: "Blank",
    boxed: "Boxed",
    ruled: "Ruled",
};

export function WritingAreaSettings({block, disabled, editor}: PrintoutBlockSettingsProps) {
    const {t} = useTranslation();
    const attrs = getWritingAreaAttrs(block.node.attrs as PrintoutWritingAreaAttrs);
    const update = (next: Partial<PrintoutWritingAreaAttrs>) => (
        updateSelectedPrintoutBlockAttributes(editor, block, next)
    );

    return (
        <>
            <PanelField label={t('Label')}>
                <TextField
                    disabled={disabled}
                    fullWidth
                    onChange={(event) => update({label: event.target.value})}
                    placeholder={t('No label yet')}
                    size="small"
                    value={attrs.label}
                />
            </PanelField>

            <PanelField label={t('Height mm')}>
                <CNumberField
                    disabled={disabled}
                    fullWidth
                    max={MAX_WRITING_AREA_HEIGHT_MM}
                    min={MIN_WRITING_AREA_HEIGHT_MM}
                    onChange={(next) => {
                        if (next !== null) update({heightMm: clampWritingAreaHeightMm(next)});
                    }}
                    size="small"
                    step={5}
                    value={attrs.heightMm}
                />
            </PanelField>

            <PanelField label={t('Style')}>
                <ToggleButtonGroup
                    disabled={disabled}
                    exclusive
                    fullWidth
                    onChange={(_event, style: WritingAreaStyle | null) => {
                        if (style) update({style});
                    }}
                    size="small"
                    value={attrs.style}
                >
                    {WRITING_AREA_STYLES.map(style => (
                        <ToggleButton key={style} sx={{textTransform: 'none'}} value={style}>
                            {t(STYLE_LABELS[style])}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </PanelField>
        </>
    );
}

export default WritingAreaSettings;
